import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    TextField,
    Button,
    Box,
    FormHelperText,
    Typography,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress
} from '@mui/material';
import { useAuth } from '../../providers/AuthProvider';
import { useAppClasses } from '../../providers/MuiThemeProvider';
import { FetchStatus } from '../../types/common';

import styles from './LoginForm.module.css';

// Page à rouvrir après la connexion CAS (le retour du CAS arrive toujours sur /login/callback) :
// après une session expirée, on revient sur la page où l'on était. Lu par AppRouter.
export const REDIRECT_AFTER_LOGIN_KEY = 'pwa_dashboard.redirectAfterLogin';

const LoginForm: React.FC = () => {
    // Extraire le paramètre 'login' de l'URL si présent (ex: ?login=web-tp-projet-07-01)
    const searchParams = new URLSearchParams(window.location.search);
    const prefilledLogin = searchParams.get('login') || '';

    const [loginValue, setLoginValue] = useState(prefilledLogin);
    const [password, setPassword] = useState('');
    const [mfaToken, setMfaToken] = useState('');
    const [showMfaModal, setShowMfaModal] = useState(false);
    const [mfaSessionData, setMfaSessionData] = useState<any>(null);
    const [mfaError, setMfaError] = useState('');
    const { login, loginWithCasTicket, getCasLoginUrl, submitMfaCode, authState, resetAuthState } = useAuth();

    const classes = useAppClasses();
    const navigate = useNavigate();

    // Connexion principale réservée au vrai CAS SSO en prod (comptes locaux réservés au dev,
    // où VITE_AUTH_MODE n'est pas défini) — voir cas_auth_resource.py côté backend.
    const isCasOnly = import.meta.env.VITE_AUTH_MODE === 'cas-only';
    const [casLoading, setCasLoading] = useState(false);
    const [casError, setCasError] = useState('');

    // Retour du CAS (ticket dans l'URL) : finalise la connexion, quel que soit le chemin exact
    // (le service_url pointe vers cette même page de login, voir CAS_LOGIN_SERVICE_URL).
    useEffect(() => {
        const ticket = new URLSearchParams(window.location.search).get('ticket');
        if (!ticket) return;

        setCasLoading(true);
        setCasError('');
        loginWithCasTicket(ticket)
            .then((result) => {
                if (!result?.success) {
                    setCasError(result?.error || 'Erreur lors de la connexion CAS');
                }
            })
            .finally(() => setCasLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCasLogin = async () => {
        setCasLoading(true);
        setCasError('');
        try {
            const { pathname, search } = window.location;
            if (!pathname.startsWith('/login')) {
                try {
                    sessionStorage.setItem(REDIRECT_AFTER_LOGIN_KEY, pathname + search);
                } catch {
                    // sessionStorage indisponible : retour à l'accueil après connexion
                }
            }
            const url = await getCasLoginUrl();
            window.location.href = url;
        } catch (error: any) {
            setCasError(error?.message || "Erreur lors de la récupération de l'URL CAS");
            setCasLoading(false);
        }
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        const result = await login(loginValue, password);
        
        // Si MFA requis, afficher le modal
        if (result && result.mfaRequired) {
            setMfaSessionData(result.sessionData);
            setShowMfaModal(true);
            setMfaError('');
        }
    };

    const handleMfaSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setMfaError('');
        
        const result = await submitMfaCode(loginValue, password, mfaToken, mfaSessionData);
        
        if (result && result.success) {
            // Fermer le modal en cas de succès
            setShowMfaModal(false);
            setMfaToken('');
            setMfaSessionData(null);
        } else if (result && result.error) {
            setMfaError(result.error);
        }
    };

    const handleMfaCancel = () => {
        setShowMfaModal(false);
        setMfaToken('');
        setMfaSessionData(null);
        setMfaError('');
        resetAuthState();
    };

    // Redirection en effet (pas pendant le render) : sur /login/callback, naviguer pendant le
    // render de LoginForm perturbait le routeur et laissait une page blanche au lieu de swapper
    // vers AuthenticatedRoutes une fois isAuthenticated passé à true.
    useEffect(() => {
        if (authState.status === FetchStatus.SUCCESS && authState.isAuthenticated) {
            navigate('/');
        }
    }, [authState.status, authState.isAuthenticated, navigate]);

    return (
        <div className={styles['main-content-loginform']}>
            <Paper className={classes.authContainer} elevation={0}>
                <Typography variant="h1" align="center" color="primary">
                    PWA-DASHBOARD
                </Typography>

                {isCasOnly ? (
                    <Box className={classes.form} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {casError ? (
                            <FormHelperText className={classes.formError}>
                                {casError}
                            </FormHelperText>
                        ) : authState.status === FetchStatus.ERROR && authState.errorMessage && (
                            // Ex. « Session expirée, veuillez vous reconnecter »
                            <FormHelperText className={classes.formError}>
                                {authState.errorMessage}
                            </FormHelperText>
                        )}
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            fullWidth
                            onClick={handleCasLogin}
                            disabled={casLoading}
                            startIcon={casLoading ? <CircularProgress size={20} color="inherit" /> : undefined}
                        >
                            {casLoading ? 'Connexion...' : 'Se connecter'}
                        </Button>
                    </Box>
                ) : (
                    <Box
                        component="form"
                        onSubmit={handleSubmit}
                        className={classes.form}
                    >
                        <TextField
                            label="Login (CAS)"
                            name="login"
                            type="text"
                            value={loginValue}
                            onChange={(e) => setLoginValue(e.target.value)}
                            required
                            fullWidth
                            variant="outlined"
                        />

                        <TextField
                            label="Mot de passe (CAS)"
                            name="password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            fullWidth
                            variant="outlined"
                        />

                        {authState.status === FetchStatus.ERROR && (
                            <FormHelperText className={classes.formError}>
                                {authState.errorMessage}
                            </FormHelperText>
                        )}

                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={authState.status === FetchStatus.LOADING}
                            size="large"
                            fullWidth
                        >
                            {authState.status === FetchStatus.LOADING ? 'Connexion...' : 'Se connecter'}
                        </Button>
                    </Box>
                )}
            </Paper>

            {/* Modal MFA */}
            <Dialog open={showMfaModal} onClose={handleMfaCancel}>
                <DialogTitle>Code de vérification requis</DialogTitle>
                <DialogContent>
                    <Box
                        component="form"
                        onSubmit={handleMfaSubmit}
                        sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2, minWidth: 300 }}
                    >
                        <Typography variant="body2" color="textSecondary">
                            Un code de vérification (OTP) est requis pour finaliser votre connexion.
                            Saisissez le code reçu sur votre application d'authentification ou par SMS.
                        </Typography>
                        
                        <TextField
                            label="Code OTP"
                            name="mfaToken"
                            type="text"
                            value={mfaToken}
                            onChange={(e) => setMfaToken(e.target.value)}
                            required
                            fullWidth
                            variant="outlined"
                            autoFocus
                            placeholder="Ex: 123456"
                        />

                        {mfaError && (
                            <FormHelperText error>
                                {mfaError}
                            </FormHelperText>
                        )}

                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleMfaCancel} color="inherit">
                        Annuler
                    </Button>
                    <Button 
                        onClick={handleMfaSubmit} 
                        variant="contained" 
                        color="primary"
                        disabled={authState.status !== FetchStatus.MFA_REQUIRED || !mfaToken}
                    >
                        {authState.status === FetchStatus.MFA_LOADING ? 'Vérification...' : 'Valider'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default LoginForm;